import { api } from '@/plugins/axios'
import { QueryFunctionContext } from '@tanstack/vue-query'
import { ticketKeys } from '@/features/tickets/api/keys'
import {
  ICashPaymentResponse,
  ICashUpgradeRequest,
  ICashUpgradeResponse,
  IFrontdesk,
  IPurchaseReceipt,
  IStripeUpgradeRequest,
  ITicket,
  ITicketCartPayload,
  ITicketCartResponse,
  ITicketCashPaymentPayload,
  ITicketChangeTierPayload,
  ITicketCheckoutResponse,
  ITicketPurchased,
  ITierChangeQuoteResponse,
} from '@/features/tickets/types'
import { AxiosResponse } from 'axios'

export const getTicketsForEvent = async ({
  queryKey,
  signal,
}: QueryFunctionContext<ReturnType<typeof ticketKeys.event>>) => {
  const [, , eventId] = queryKey
  const response = await api.get<{ tiers: ITicket[] }>(`/events/${eventId}/available-tiers`, { signal })
  return response.data
}

export const postCart = async ({ eventId, data }: { eventId: number; data: ITicketCartPayload }) => {
  const response = await api.post<ITicketCartPayload, AxiosResponse<ITicketCartResponse>>(
    `/events/${eventId}/cart`,
    data
  )
  return response.data
}

export const postCheckout = async ({ eventId, data }: { eventId: number; data: ITicketCartPayload }) => {
  const response = await api.post<ITicketCartPayload, AxiosResponse<ITicketCheckoutResponse>>(
    `/events/${eventId}/checkout`,
    data
  )
  return response.data
}

export const getMyTicketsForEvent = async ({
  queryKey,
  signal,
}: QueryFunctionContext<ReturnType<typeof ticketKeys.myTickets>>) => {
  const [, , eventId] = queryKey
  const response = await api.get<ITicketPurchased[]>(`/events/${eventId}/my-tickets`, { signal })
  return response.data
}

export const getUserTicketsForEvent = async ({
  queryKey,
  signal,
}: QueryFunctionContext<ReturnType<typeof ticketKeys.userTickets>>) => {
  const [, , eventId, userId] = queryKey
  const response = await api.get<ITicketPurchased[]>(`/events/${eventId}/user-tickets/${userId}`, { signal })
  return response.data
}

export const assignTicketToUser = async ({
  ticketId,
  userId,
  email,
  fullName,
}: {
  ticketId: number
  userId?: number
  email?: string
  fullName?: string
}) => {
  if (!userId && !email) {
    throw new Error('Either userId or email must be provided')
  }
  type Payload = { userId: number } | { email: string; fullName?: string }
  const payload: Payload = userId ? { userId } : { email: email!, fullName: fullName || '' }
  const response = await api.post<
    Payload,
    AxiosResponse<{
      success: boolean
    }>
  >(`/tickets/${ticketId}/assign`, payload)
  return response.data
}

export const getFrontdesk = async ({
  queryKey,
  signal,
}: QueryFunctionContext<ReturnType<typeof ticketKeys.frontdesk>>) => {
  const [, , eventId] = queryKey
  const response = await api.get<IFrontdesk>(`/events/${eventId}/tickets`, { signal })
  return response.data
}

export const getTicketById = async ({
  queryKey,
  signal,
}: QueryFunctionContext<ReturnType<typeof ticketKeys.ticketById>>) => {
  const [, , ticketId] = queryKey
  if (!ticketId) return null
  const response = await api.get<ITicketPurchased>(`/tickets/${ticketId}`, { signal })
  return response.data
}

export const getTicketByQRCode = async ({
  queryKey,
  signal,
}: QueryFunctionContext<ReturnType<typeof ticketKeys.ticketByQRCode>>) => {
  const [, , , qrCode] = queryKey
  if (!qrCode) return null
  const response = await api.get<ITicketPurchased>(`/tickets/qr/${qrCode}`, { signal })
  return response.data
}

export const postCashPayment = async ({ eventId, data }: { eventId: number; data: ITicketCashPaymentPayload }) => {
  const response = await api.post<ITicketCashPaymentPayload, AxiosResponse<ICashPaymentResponse>>(
    `/events/${eventId}/cash-payment`,
    data
  )
  return response.data
}

export const changeTicketTier = async ({
  ticketId,
  data,
}: {
  ticketId: number
  data: ITicketChangeTierPayload
}): Promise<ITierChangeQuoteResponse> => {
  const response = await api.post<ITicketChangeTierPayload, AxiosResponse<ITierChangeQuoteResponse>>(
    `/tickets/${ticketId}/change-tier`,
    data
  )
  return response.data
}

export const postCashTierChange = async ({ ticketId, data }: { ticketId: number; data: ICashUpgradeRequest }) => {
  const response = await api.post<ICashUpgradeRequest, AxiosResponse<ICashUpgradeResponse>>(
    `/tickets/${ticketId}/cash-upgrade`,
    data
  )
  return response.data
}

export const postStripeTierChangeCheckout = async ({
  ticketId,
  data,
}: {
  ticketId: number
  data: IStripeUpgradeRequest
}) => {
  const response = await api.post<IStripeUpgradeRequest, AxiosResponse<ITicketCheckoutResponse>>(
    `/tickets/${ticketId}/stripe-upgrade`,
    data
  )
  return response.data
}

export const getPurchaseReceipt = async ({
  queryKey,
  signal,
}: QueryFunctionContext<ReturnType<typeof ticketKeys.receipt>>) => {
  const [, , purchaseId] = queryKey
  const response = await api.get<IPurchaseReceipt>(`/purchases/${purchaseId}/receipt`, { signal })
  return response.data
}

export const checkInTicket = async (ticketId: number) => {
  const response = await api.put(`/tickets/${ticketId}`)
  return response.data
}

export const refundTicket = async (ticketId: number) => {
  const response = await api.post(`/tickets/${ticketId}/refund`)
  return response.data
}

// Per-ticket admin uncheck (inverse of check-in for a single ticket).
export const uncheckTicket = async (ticketId: number) => {
  const response = await api.post(`/tickets/${ticketId}/uncheck`)
  return response.data
}

// Undo an erroneous admin ticket-add (no-money tickets only). Not a refund.
export const removeTicket = async (ticketId: number) => {
  const response = await api.delete(`/tickets/${ticketId}`)
  return response.data
}
